import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { WorkItem } from '@/api/types'

const MIRROR_KEY = 'hw_mirror_works_v1'

function loadMirror(): WorkItem[] {
  try {
    const raw = localStorage.getItem(MIRROR_KEY)
    const list = raw ? (JSON.parse(raw) as WorkItem[]) : []
    return list.filter((w) => !w.url.startsWith('blob:'))
  } catch {
    return []
  }
}

function saveMirror(list: WorkItem[]) {
  try {
    localStorage.setItem(
      MIRROR_KEY,
      JSON.stringify(list.filter((w) => !w.url.startsWith('blob:')).slice(0, 300)),
    )
  } catch {
    /* ignore */
  }
}

type WorksApi = {
  works: WorkItem[]
  addMany: (items: WorkItem[]) => void
  toggleFavorite: (id: string) => void
  remove: (id: string) => void
  clear: () => void
  stats: { total: number; images: number; videos: number; texts: number; favorites: number }
}

const Ctx = createContext<WorksApi | null>(null)

export function WorksProvider({ children }: { children: ReactNode }) {
  const [works, setWorks] = useState<WorkItem[]>(() => loadMirror())

  useEffect(() => saveMirror(works), [works])

  const addMany = useCallback((items: WorkItem[]) => {
    if (!items.length) return
    setWorks((list) => {
      const seen = new Set(list.map((w) => w.archiveKey))
      const fresh = items.filter((i) => !seen.has(i.archiveKey))
      return [...fresh, ...list].slice(0, 300)
    })
  }, [])

  const toggleFavorite = useCallback((id: string) => {
    setWorks((list) => list.map((w) => (w.id === id ? { ...w, favorite: !w.favorite } : w)))
  }, [])

  const remove = useCallback((id: string) => {
    setWorks((list) => list.filter((w) => w.id !== id))
  }, [])

  const clear = useCallback(() => setWorks([]), [])

  const stats = useMemo(
    () => ({
      total: works.length,
      images: works.filter((w) => w.workType === 'image').length,
      videos: works.filter((w) => w.workType === 'video').length,
      texts: works.filter((w) => w.workType === 'text').length,
      favorites: works.filter((w) => w.favorite).length,
    }),
    [works],
  )

  const value = useMemo<WorksApi>(
    () => ({ works, addMany, toggleFavorite, remove, clear, stats }),
    [works, addMany, toggleFavorite, remove, clear, stats],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useWorks() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useWorks must be used within WorksProvider')
  return ctx
}
